import { useState, useEffect } from 'react';
import { LineIcon, type LineIconName } from './PreGame';
import { StampIcon } from './Icons';

const StepIcon = ({ name }: { name: LineIconName }) => (
  <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-base-200 text-primary">
    <LineIcon name={name} className="w-8 h-8" />
  </span>
);

const Tile = ({ icon, value, label }: { icon: LineIconName; value: string; label: string }) => (
  <div className="rounded-xl border border-base-300 bg-base-200/50 p-3 text-center">
    <LineIcon name={icon} className="w-5 h-5 mx-auto mb-1 text-primary" />
    <div className="font-numeric">{value}</div>
    <div className="text-xs text-base-content/60">{label}</div>
  </div>
);

interface WelcomeModalProps {
  username: string;
  stamps: number;
  onClose: () => void;
}

export default function WelcomeModal({ username, stamps, onClose }: WelcomeModalProps) {
  const [step, setStep] = useState(0);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    // Animate in
    setTimeout(() => setIsVisible(true), 100);
  }, []);

  const handleClose = () => {
    setIsVisible(false);
    setTimeout(onClose, 300);
  };

  const steps = [
    {
      title: `Welcome, ${username}!`,
      content: (
        <div className="text-center space-y-4">
          <StepIcon name="sparkles" />
          <p className="text-lg">
            You're now part of <span className="font-bold text-primary">Choose.place</span>!
          </p>
          <p className="opacity-80">
            Test your geography knowledge with fun games and compete with players worldwide.
          </p>
        </div>
      ),
    },
    {
      title: 'Your Starter Pack',
      content: (
        <div className="text-center space-y-4">
          <div className="flex items-center justify-center gap-3">
            <span className="flex h-16 w-16 items-center justify-center rounded-full bg-base-200 text-primary">
              <StampIcon size={32} />
            </span>
            <div className="text-left">
              <div className="font-numeric text-4xl text-primary">{stamps}</div>
              <div className="text-sm opacity-80">stamps</div>
            </div>
          </div>
          <p className="opacity-80">
            We've given you <span className="font-bold">{stamps} stamps</span> to get started!
          </p>
          <div className="bg-base-200 rounded-lg p-4 text-sm space-y-2">
            <div className="flex justify-between">
              <span>Registration bonus</span>
              <span className="font-numeric text-primary">+12</span>
            </div>
            <div className="flex justify-between">
              <span>Welcome medal</span>
              <span className="font-numeric text-primary">+2</span>
            </div>
          </div>
        </div>
      ),
    },
    {
      title: 'How Stamps Work',
      content: (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <Tile icon="play" value="1 stamp" label="per game" />
            <Tile icon="calendar" value="+5 stamps" label="daily login" />
            <Tile icon="trophy" value="+1-8 stamps" label="medals earned" />
            <Tile icon="globe" value="+1 stamp" label="country mastered" />
          </div>
          <p className="text-center text-sm opacity-80">
            Earn stamps by playing daily, unlocking medals, and mastering countries!
          </p>
        </div>
      ),
    },
    {
      title: "You're Ready!",
      content: (
        <div className="text-center space-y-4">
          <StepIcon name="map" />
          <p className="text-lg">
            Start with <span className="font-bold">Memory Map</span> - match flags with countries!
          </p>
          <div className="flex flex-wrap gap-2 justify-center">
            <span className="badge chip-quiet px-3">Memory Map</span>
            <span className="badge chip-quiet px-3">Daily Challenge</span>
            <span className="badge chip-quiet px-3">Leaderboards</span>
          </div>
          <p className="text-sm opacity-70">
            Good luck and have fun exploring the world!
          </p>
        </div>
      ),
    },
  ];

  const currentStep = steps[step];
  const isLastStep = step === steps.length - 1;

  return (
    <div className={`fixed inset-0 z-[100] flex items-center justify-center p-4 transition-all duration-300 ${isVisible ? 'opacity-100' : 'opacity-0'}`}>
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={handleClose}
      />

      {/* Modal */}
      <div className={`relative bg-base-100 rounded-[1.25rem] border border-base-300 shadow-2xl max-w-md w-full overflow-hidden transition-all duration-300 ${isVisible ? 'scale-100 translate-y-0' : 'scale-95 translate-y-4'}`}>
        {/* Progress dots */}
        <div className="absolute top-4 left-1/2 -translate-x-1/2 flex gap-2">
          {steps.map((_, i) => (
            <div
              key={i}
              className={`w-2 h-2 rounded-full transition-colors ${i === step ? 'bg-primary' : 'bg-base-300'}`}
            />
          ))}
        </div>

        {/* Content */}
        <div className="p-8 pt-12">
          <h2 className="font-comic text-3xl leading-tight text-center mb-6">{currentStep.title}</h2>
          {currentStep.content}
        </div>

        {/* Actions */}
        <div className="p-4 bg-base-200/50 flex gap-3">
          {step > 0 && (
            <button
              className="btn btn-ghost flex-1"
              onClick={() => setStep(step - 1)}
            >
              Back
            </button>
          )}
          {step === 0 && (
            <button
              className="btn btn-ghost flex-1"
              onClick={handleClose}
            >
              Skip
            </button>
          )}
          <button
            className={`btn flex-1 ${isLastStep ? 'btn-primary' : 'btn-primary'}`}
            onClick={isLastStep ? handleClose : () => setStep(step + 1)}
          >
            {isLastStep ? "Let's Play!" : 'Next'}
          </button>
        </div>
      </div>
    </div>
  );
}
